import produce from "immer";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { SecondaryButton } from "@/components/elements/Button";
import FieldLabel from "@/components/primitives/InputLabel";
import { FormType } from "@/integrations";
import { snakeCase } from "lodash";

import ShopifyCustomReport from "./ShopifyCustomReport";

export type ShopifyReport = {
  id: string;
  name: string;
  query: string;
  columns: { name: string; dataType: string }[];
  valid: boolean;
};

export default function ShopifyForm(_props: {
  field: Extract<FormType, { type: "shopify-custom-reports" }>;
}) {
  const context = useFormContext();
  const connectionId = context.getValues("connectionId");

  const [reportErrors, setReportErrors] = useState<Record<string, string>>({});
  const [invalidReportIds, setInvalidReportIds] = useState<string[]>([]);

  const invalidReportIdsRef = useRef(invalidReportIds);
  invalidReportIdsRef.current = invalidReportIds;

  const reportErrorsRef = useRef(reportErrors);
  reportErrorsRef.current = reportErrors;

  useEffect(() => {
    context.register("custom_reports", {
      required: false,
      validate: () => {
        if (invalidReportIdsRef.current.length > 0) return "Validation error";
        if (Object.keys(reportErrorsRef.current).length > 0)
          return "Validation error";
        return true;
      },
    });
  }, [context]);

  const getInitialReports = () => {
    const values = context.getValues()["custom_reports"];
    if (values === "NO_REPORTS") return [];
    // Reports saved outside this form can lack `query` or `columns`.
    return (
      values
        ?.filter((v: any) => !!v)
        .map((v: any) => ({
          ...v,
          query: v.query ?? "",
          columns: v.columns ?? [],
        })) ?? []
    );
  };
  const [reports, setReports] = useState<ShopifyReport[]>(getInitialReports());

  const changeReport = (report: ShopifyReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        const cur = draft.find((rep) => rep.id === report.id);
        if (cur) {
          cur.name = report.name;
          cur.query = report.query;
          cur.columns = report.columns;
          cur.valid = report.valid;
        }
      }),
    );
  };

  const removeReport = (report: ShopifyReport) => {
    setReports((prev) =>
      produce(prev, (draft) => {
        draft = draft.filter((rep) => rep.id !== report.id);
        return draft;
      }),
    );
    setInvalidReportIds((prev) => prev.filter((x) => x !== report.id));
  };

  const onReportInvalid = useCallback((reportId: string) => {
    setInvalidReportIds((prev) => [...prev, reportId]);
  }, []);

  const onReportValid = useCallback((reportId: string) => {
    setInvalidReportIds((prev) => prev.filter((x) => x !== reportId));
  }, []);

  const setValue = context.setValue;
  const setError = context.setError;
  const trigger = context.trigger;

  useEffect(() => {
    if (reports.length === 0) {
      setValue("custom_reports", [], { shouldValidate: false });
      trigger("custom_reports");
    }
  }, [reports.length, setValue, trigger]);

  useEffect(() => {
    if (invalidReportIds.length > 0) {
      setError("custom_reports", { message: "Validation error" });
      trigger("custom_reports");
      return;
    }

    const getReports = () => {
      if (!reports.length) return [];
      const foundReports = reports.every(
        (r) => !!r.name && !!r.query.length && !!r.columns.length,
      )
        ? reports
        : undefined;
      return foundReports;
    };

    const currentReports = getReports();
    if (currentReports && Array.isArray(currentReports)) {
      const duplicateReportNames = currentReports.filter(
        (x) =>
          currentReports.filter((y) => snakeCase(y.name) === snakeCase(x.name))
            .length > 1,
      );

      const _reportErrors: Record<string, string> = {};
      for (const duplicateReport of duplicateReportNames) {
        _reportErrors[duplicateReport.id] =
          `Report names must be unique in snake_case format. Another report with the identifier "${snakeCase(duplicateReport.name)}" already exists.`;
      }

      setReportErrors(_reportErrors);
      if (duplicateReportNames.length > 0) {
        setError("custom_reports", { message: "Validation error" });
        trigger("custom_reports");
      } else {
        setValue("custom_reports", currentReports, { shouldValidate: true });
      }
    } else {
      trigger("custom_reports");
    }
  }, [reports, setValue, setError, trigger, invalidReportIds.length]);

  return (
    <div className="space-y-4">
      <FieldLabel>Custom reports ({reports.length}/20)</FieldLabel>
      {!!reports.length && (
        <div>
          <div className="flex flex-col gap-4">
            {reports.map((r) => (
              <ShopifyCustomReport
                report={r}
                key={r.id}
                onChangeReport={changeReport}
                onRemoveReport={removeReport}
                validationError={reportErrors[r.id]}
                connectionId={connectionId}
                onReportInvalid={onReportInvalid}
                onReportValid={onReportValid}
              />
            ))}
          </div>
        </div>
      )}
      <SecondaryButton
        onClick={() =>
          setReports((prev) => [
            ...prev,
            { id: uuid(), query: "", name: "", columns: [], valid: false },
          ])
        }
      >
        Add custom report
      </SecondaryButton>
    </div>
  );
}
